import type React from 'react'
import type { AccountRequest } from '@/types/api/events/allocation-update'

import Icon, { IconName } from '@/components/atomic/icon'
import { BodySubtext } from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { PLACEHOLDER } from '@/utils/presentation/formatting'

/**
 * Renders a prime brokerage status icon based on the enabled/disabled/unknown state
 */
export const renderPrimeBrokerageStatusIcon = (
  isEnabled: boolean | undefined,
): React.JSX.Element => {
  if (isEnabled === undefined) {
    return <BodySubtext className="text-primary">{PLACEHOLDER}</BodySubtext>
  }

  return isEnabled ? (
    <Tooltip simpleText="Eligible" side="right">
      {/* This is needed because the icon is an SVG that does not support pointer-events */}
      <div className="pointer-events-auto">
        <Icon
          iconName={IconName.CheckCircleIcon}
          className="text-moment-green-800 dark:text-moment-green-600"
          outline
        />
      </div>
    </Tooltip>
  ) : (
    <Tooltip simpleText="Ineligible" side="right">
      {/* This is needed because the icon is an SVG that does not support pointer-events */}
      <div className="pointer-events-auto">
        <Icon
          iconName={IconName.XCircleIcon}
          className="text-moment-red-700 dark:text-moment-red-500"
          outline
        />
      </div>
    </Tooltip>
  )
}

/**
 * Gets the prime brokerage status for a single account request from its account_configuration.
 */
export const getPrimeBrokerageStatusForAccountRequest = (
  accountRequest: AccountRequest | undefined,
): boolean | undefined => {
  return accountRequest?.account_configuration?.prime_brokerage_enabled
}

/**
 * Gets the prime brokerage status for a specific account by looking it up
 * in a list of account requests via account_configuration.
 */
export const getPrimeBrokerageStatusForAccount = (
  accountId: string,
  accountRequests: ReadonlyArray<AccountRequest> | null | undefined,
): boolean | undefined => {
  if (!accountRequests) {
    return undefined
  }
  const request = accountRequests.find((r) => r.account_id === accountId)
  return request?.account_configuration?.prime_brokerage_enabled
}

/**
 * Renders prime brokerage icon for a single account, looked up from account requests.
 */
export const renderPrimeBrokerageIconForAccount = (
  accountId: string,
  accountRequests: ReadonlyArray<AccountRequest> | null | undefined,
): React.JSX.Element => {
  const isEnabled = getPrimeBrokerageStatusForAccount(
    accountId,
    accountRequests,
  )
  return renderPrimeBrokerageStatusIcon(isEnabled)
}

/**
 * Aggregate prime brokerage status for a block trade.
 * - All accounts enabled = enabled (true)
 * - All accounts unknown = unknown (undefined)
 * - Mixed or all disabled = disabled (false)
 */
export const getPrimeBrokerageStatusForBlockTrade = (
  accountRequests: ReadonlyArray<AccountRequest>,
): boolean | undefined => {
  if (!accountRequests || accountRequests.length === 0) {
    return undefined
  }

  if (accountRequests.length === 1) {
    return getPrimeBrokerageStatusForAccountRequest(accountRequests[0])
  }

  const numAccounts = accountRequests.length
  let numEnabled = 0
  let numUnknown = 0
  for (const request of accountRequests) {
    const status = getPrimeBrokerageStatusForAccountRequest(request)
    if (status === true) {
      numEnabled++
    } else if (status === undefined) {
      numUnknown++
    }
  }

  if (numAccounts === numEnabled) {
    return true
  }
  if (numAccounts === numUnknown) {
    return undefined
  }
  return false
}

/**
 * Resolves the prime brokerage status for an order row, dispatching on whether
 * it is a block trade. Single source of truth shared by the cell icon, the
 * column filter, and sorting.
 */
export const getOrderPrimeBrokerageStatus = (data: {
  is_block_trade?: boolean
  account_id?: string
  account_requests?: ReadonlyArray<AccountRequest> | null
}): boolean | undefined => {
  if (data.is_block_trade) {
    return getPrimeBrokerageStatusForBlockTrade(data.account_requests ?? [])
  }
  return getPrimeBrokerageStatusForAccount(
    data.account_id ?? '',
    data.account_requests,
  )
}

/**
 * Renders prime brokerage icon for block trades with aggregate logic.
 * - All accounts enabled = green checkmark
 * - All accounts unknown = placeholder
 * - Mixed or all disabled = red X
 */
export const renderPrimeBrokerageIconForBlockTrade = (
  accountRequests: ReadonlyArray<AccountRequest>,
): React.JSX.Element => {
  return renderPrimeBrokerageStatusIcon(
    getPrimeBrokerageStatusForBlockTrade(accountRequests),
  )
}
